import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

export interface ArenaRules {
  noHateSpeech: boolean;
  beRespectful: boolean;
  stayOnTopic: boolean;
  noSpamming: boolean;
  customRules: string[];
}

export interface PinnedMessage {
  id: string;
  content: string;
  sender_name: string;
  pinned_at: string;
}

const DEFAULT_RULES: ArenaRules = {
  noHateSpeech: true,
  beRespectful: true,
  stayOnTopic: true,
  noSpamming: true,
  customRules: [],
};

export const useArenaModerationTools = (arenaId: string | null) => {
  const { user } = useAuthContext();
  const [rules, setRules] = useState<ArenaRules>(DEFAULT_RULES);
  const [pinnedMessages, setPinnedMessages] = useState<PinnedMessage[]>([]);
  const [isMuteAllActive, setIsMuteAllActive] = useState(false);

  // Subscribe to moderation changes via realtime
  useEffect(() => {
    if (!arenaId) return;

    const channel = supabase.channel(`arena-moderation-${arenaId}`);

    channel
      .on('broadcast', { event: 'mute_all' }, (payload) => {
        setIsMuteAllActive(payload.payload.active);
        if (payload.payload.active) {
          toast({
            title: 'All muted',
            description: 'The host has muted all participants',
          });
        }
      })
      .on('broadcast', { event: 'rules_updated' }, (payload) => {
        setRules(payload.payload.rules);
      })
      .on('broadcast', { event: 'message_pinned' }, (payload) => {
        setPinnedMessages((prev) => [...prev, payload.payload.message]);
        toast({
          title: 'Message pinned 📌',
          description: payload.payload.message.content.slice(0, 50) + '...',
        });
      })
      .on('broadcast', { event: 'message_unpinned' }, (payload) => {
        setPinnedMessages((prev) =>
          prev.filter((m) => m.id !== payload.payload.messageId)
        );
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [arenaId]);

  // Mute all participants
  const muteAll = useCallback(async () => {
    if (!arenaId || !user) return false;

    try {
      // Update all participants except the host
      const { error } = await supabase
        .from('arena_participants')
        .update({ is_muted: true })
        .eq('arena_id', arenaId)
        .neq('user_id', user.id);

      if (error) throw error;

      // Broadcast mute all event
      const channel = supabase.channel(`arena-moderation-${arenaId}`);
      await channel.send({
        type: 'broadcast',
        event: 'mute_all',
        payload: { active: true },
      });

      setIsMuteAllActive(true);
      toast({
        title: 'All muted 🔇',
        description: 'All participants have been muted',
      });
      return true;
    } catch (error) {
      console.error('Error muting all:', error);
      return false;
    }
  }, [arenaId, user]);

  // Unmute all participants
  const unmuteAll = useCallback(async () => {
    if (!arenaId) return false;

    try {
      // Broadcast unmute all event
      const channel = supabase.channel(`arena-moderation-${arenaId}`);
      await channel.send({
        type: 'broadcast',
        event: 'mute_all',
        payload: { active: false },
      });

      setIsMuteAllActive(false);
      toast({
        title: 'Unmute allowed 🔊',
        description: 'Participants can now unmute themselves',
      });
      return true;
    } catch (error) {
      console.error('Error unmuting all:', error);
      return false;
    }
  }, [arenaId]);

  // Toggle mute all
  const toggleMuteAll = useCallback(async () => {
    if (isMuteAllActive) {
      return unmuteAll();
    } else {
      return muteAll();
    }
  }, [isMuteAllActive, muteAll, unmuteAll]);

  // Update arena rules
  const updateRules = useCallback(
    async (newRules: ArenaRules) => {
      if (!arenaId) return false;

      try {
        // Broadcast rules update
        const channel = supabase.channel(`arena-moderation-${arenaId}`);
        await channel.send({
          type: 'broadcast',
          event: 'rules_updated',
          payload: { rules: newRules },
        });

        setRules(newRules);
        toast({
          title: 'Rules updated ✓',
          description: 'Arena rules have been updated',
        });
        return true;
      } catch (error) {
        console.error('Error updating rules:', error);
        return false;
      }
    },
    [arenaId]
  );

  // Add custom rule
  const addCustomRule = useCallback(
    async (rule: string) => {
      const newRules = {
        ...rules,
        customRules: [...rules.customRules, rule],
      };
      return updateRules(newRules);
    },
    [rules, updateRules]
  );

  // Remove custom rule
  const removeCustomRule = useCallback(
    async (index: number) => {
      const newRules = {
        ...rules,
        customRules: rules.customRules.filter((_, i) => i !== index),
      };
      return updateRules(newRules);
    },
    [rules, updateRules]
  );

  // Pin a message
  const pinMessage = useCallback(
    async (messageId: string, content: string, senderName: string) => {
      if (!arenaId) return false;

      try {
        const pinnedMessage: PinnedMessage = {
          id: messageId,
          content,
          sender_name: senderName,
          pinned_at: new Date().toISOString(),
        };

        // Broadcast pin event
        const channel = supabase.channel(`arena-moderation-${arenaId}`);
        await channel.send({
          type: 'broadcast',
          event: 'message_pinned',
          payload: { message: pinnedMessage },
        });

        setPinnedMessages((prev) => [...prev, pinnedMessage]);
        return true;
      } catch (error) {
        console.error('Error pinning message:', error);
        return false;
      }
    },
    [arenaId]
  );

  // Unpin a message
  const unpinMessage = useCallback(
    async (messageId: string) => {
      if (!arenaId) return false;

      try {
        // Broadcast unpin event
        const channel = supabase.channel(`arena-moderation-${arenaId}`);
        await channel.send({
          type: 'broadcast',
          event: 'message_unpinned',
          payload: { messageId },
        });

        setPinnedMessages((prev) => prev.filter((m) => m.id !== messageId));
        return true;
      } catch (error) {
        console.error('Error unpinning message:', error);
        return false;
      }
    },
    [arenaId]
  );

  // Mute a specific participant
  const muteParticipant = useCallback(
    async (userId: string) => {
      if (!arenaId) return false;

      try {
        const { error } = await supabase
          .from('arena_participants')
          .update({ is_muted: true })
          .eq('arena_id', arenaId)
          .eq('user_id', userId);

        if (error) throw error;
        return true;
      } catch (error) {
        console.error('Error muting participant:', error);
        return false;
      }
    },
    [arenaId]
  );

  // Remove participant from arena
  const removeParticipant = useCallback(
    async (userId: string) => {
      if (!arenaId) return false;

      try {
        const { error } = await supabase
          .from('arena_participants')
          .update({ left_at: new Date().toISOString() })
          .eq('arena_id', arenaId)
          .eq('user_id', userId);

        if (error) throw error;

        toast({
          title: 'Participant removed',
          description: 'The user has been removed from the arena',
        });
        return true;
      } catch (error) {
        console.error('Error removing participant:', error);
        return false;
      }
    },
    [arenaId]
  );

  return {
    rules,
    pinnedMessages,
    isMuteAllActive,
    muteAll,
    unmuteAll,
    toggleMuteAll,
    updateRules,
    addCustomRule,
    removeCustomRule,
    pinMessage,
    unpinMessage,
    muteParticipant,
    removeParticipant,
  };
};
